import * as Radix from '@radix-ui/react-popover'
import { cn } from '../../lib/cn.js'
import { usePortalTarget } from './PortalTarget.js'

/**
 * A panel anchored to what opened it.
 *
 * Distinct from a dialog: a dialog takes the whole screen's attention and returns
 * you afterwards; a popover is part of the control it hangs off, and clicking
 * anywhere else simply closes it. A date picker belongs in one, a confirmation
 * does not.
 */
export function Popover({
  trigger,
  children,
  open,
  onOpenChange,
  align = 'start',
  className,
}: {
  trigger: React.ReactNode
  children: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  align?: 'start' | 'center' | 'end'
  className?: string
}) {
  /* Into the sheet or the dialog when there is one. See `PortalTarget`: a
     popover portalled to the body from inside a dialog cannot be scrolled. */
  const container = usePortalTarget()
  return (
    <Radix.Root open={open} onOpenChange={onOpenChange}>
      <Radix.Trigger asChild>{trigger}</Radix.Trigger>
      <Radix.Portal {...(container ? { container } : {})}>
        <Radix.Content
          align={align}
          sideOffset={6}
          className={cn(
            'z-50 rounded-md border border-hairline bg-surface p-3 shadow-lg outline-none',
            className,
          )}
        >
          {children}
        </Radix.Content>
      </Radix.Portal>
    </Radix.Root>
  )
}
